/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import { CdsDisclosure, type Props$1 as CdsDisclosureProps } from '@coursera-web/cds-labs';

import { Skeleton } from '@coursera/cds-core';
import type { CourseItem, Product } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { CoachBrandLabel, CoachBrandLabelWithBeta } from 'bundles/ai-coach-platform/components/branding/CoachBranding';

import _t from 'i18n!nls/ai-coach-platform';

import ShowMoreDisclosureButtons from './ShowMoreDisclosureButtons';

const styles = {
  skeleton: css`
    /* override default CDS Skeleton styles */
    .cds-Skeleton-root {
      background-color: var(--cds-color-emphasis-primary-background-weak);
    }
  `,
};

const DisclosureSkeleton = () => {
  return (
    <div className="coach-disclosure-skeleton" css={styles.skeleton}>
      <Skeleton variant="text" height={24} />
      <Skeleton variant="text" width="50%" height={24} />

      <div
        css={css`
          display: flex;
          gap: 8px;
          margin: var(--cds-spacing-200) 0;
        `}
      >
        <Skeleton
          variant="rect"
          width={120}
          height={32} // match the Coach trigger buttons
          css={css`
            border-radius: var(--cds-border-radius-100);
          `}
        />
        <Skeleton
          variant="rect"
          width={120}
          height={32} // match the Coach trigger buttons
          css={css`
            border-radius: var(--cds-border-radius-100);
          `}
        />
      </div>
    </div>
  );
};

export type Props = Omit<CdsDisclosureProps, 'header' | 'aria-label'> & {
  'aria-label'?: string;

  /**
   * List of buttons to render within the Disclosure footer area.
   */
  actionButtons?: React.ReactNode[];

  /**
   * Custom footer to render for the Disclosure footer.
   */
  customFooter?: React.ReactNode;

  /**
   * Shows "Beta" next to the Coach header logo
   */
  showBetaTag?: boolean;
  /**
   * Shows a skeleton version of the disclosure
   * @default false
   */
  loading?: boolean;

  additionalStyles?: SerializedStyles;

  mode: string;
  itemId?: string;
  courseId?: string;
};

/**
 * Simple wrapper around CDS Disclosure for Coach-specific use cases.
 */
export const CoachDisclosure = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const {
    loading = false,
    showBetaTag = false,
    actionButtons,
    customFooter,
    'aria-label': ariaLabel = _t('#{coachLabel}, AI powered assistant, beta', { coachLabel: 'Coach' }),
    children,
    onToggle,
    additionalStyles,
    mode,
    itemId,
    courseId,
    defaultExpanded = true,
    ...rest
  } = props;

  const track = useTracker();

  const showFooter = loading ? false : !!actionButtons || !!customFooter;

  const sharedEventingData = {
    courseItem: {
      id: itemId as string,
    } as CourseItem,
    product: {
      type: 'course',
      id: courseId as string,
    } as Product,
  };

  const disclosureVisibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_coach',
    {
      coachViewType: 'view_coach_disclosure',
      coachWidgetState: defaultExpanded ? 'open' : 'closed',
      coach: {
        mode,
      },
      ...(courseId && { product: sharedEventingData.product }),
      ...(itemId && { courseItem: sharedEventingData.courseItem }),
    }
  );

  const handleToggle = (expanded: boolean) => {
    onToggle?.(expanded);

    track('interact_coach', {
      coachInteractionType: expanded ? 'coach_disclosure_expanded' : 'coach_disclosure_collapsed',
      coach: {
        mode,
      },
      ...(courseId && { product: sharedEventingData.product }),
      ...(itemId && { courseItem: sharedEventingData.courseItem }),
    });
  };

  const footerContent =
    customFooter ?? (actionButtons ? <ShowMoreDisclosureButtons>{actionButtons}</ShowMoreDisclosureButtons> : null);

  return (
    <div ref={disclosureVisibilityTrackingRef}>
      <CdsDisclosure
        {...rest}
        defaultExpanded={defaultExpanded}
        ref={ref}
        aria-label={ariaLabel}
        header={
          <div css={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {showBetaTag ? <CoachBrandLabelWithBeta /> : <CoachBrandLabel />}
          </div>
        }
        footer={showFooter && footerContent}
        onToggle={handleToggle}
        css={additionalStyles}
      >
        {loading && <DisclosureSkeleton />}
        {!loading && children}
      </CdsDisclosure>
    </div>
  );
});

export default CoachDisclosure;
